import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FiArrowRight } from "react-icons/fi";

export default function Hero() {
  const reduce = useReducedMotion();
  const [showPhoto, setShowPhoto] = React.useState(true);

  const enter = (duration, delay = 0) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          transition: { duration, delay },
        };

  return (
    <section id="home" className="relative border-b-2 border-black/10 dark:border-white/10 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-20 lg:pt-20 lg:pb-28">
        <div className="lg:grid lg:grid-cols-12 lg:gap-10 lg:items-end">
          <div className="lg:col-span-7 border-l-4 border-black dark:border-white pl-5 sm:pl-8">
            <motion.p
              {...(reduce
                ? {}
                : {
                    initial: { opacity: 0, y: 10 },
                    animate: { opacity: 1, y: 0 },
                    transition: { duration: 0.5 },
                  })}
              className="inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.35em] text-stone-500 dark:text-stone-400"
            >
              Product engineering for serious builds
            </motion.p>

            <motion.h1
              {...enter(0.65)}
              className="mt-5 font-display text-4xl sm:text-5xl lg:text-7xl font-semibold leading-[1.02] text-balance text-black dark:text-white"
            >
              I build revenue-ready products, not just screens.
            </motion.h1>
            <motion.p
              {...enter(0.75, 0.08)}
              className="mt-6 text-lg text-stone-600 dark:text-stone-400 max-w-xl text-pretty leading-relaxed"
            >
              I help founders and teams turn complex mobile, payment, marketplace, and backend ideas
              into stable products that can launch, charge users, and keep running.
            </motion.p>
            <motion.div
              {...enter(0.8, 0.16)}
              className="mt-10 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3"
            >
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-black text-white dark:bg-white dark:text-black px-6 py-3.5 font-mono font-bold uppercase tracking-wider text-xs hover:bg-stone-800 dark:hover:bg-stone-200 transition-colors"
              >
                Start a project <FiArrowRight className="shrink-0" aria-hidden />
              </a>
              <a
                href="#pricing"
                className="inline-flex items-center justify-center gap-2 border-2 border-black dark:border-white px-6 py-3.5 font-mono font-bold uppercase tracking-wider text-xs hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors"
              >
                See rates
              </a>
            </motion.div>
          </div>

          <motion.aside
            {...(reduce
              ? {}
              : {
                  initial: "hidden",
                  animate: "show",
                  variants: {
                    hidden: {},
                    show: { transition: { staggerChildren: 0.1, delayChildren: 0.3 } },
                  },
                })}
            className="hidden lg:block lg:col-span-5 mt-0"
          >
            <div className="border-2 border-black/15 dark:border-white/15 bg-white dark:bg-black">
              {showPhoto ? (
                <img
                  src={`${process.env.PUBLIC_URL}/images/file_00000000b73071f496914cddf378e74c.png`}
                  alt="Natnael Adane"
                  onError={() => setShowPhoto(false)}
                  className="h-[420px] w-full object-cover object-center"
                />
              ) : (
                <div className="h-[420px] p-8 flex flex-col justify-end bg-black text-white dark:bg-white dark:text-black">
                  <p className="font-display text-5xl font-extrabold leading-none">Natnael Adane</p>
                  <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-white/60 dark:text-black/60">
                    Product engineer | founder | full-stack builder
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-px bg-black/15 dark:bg-white/15 border-t-2 border-black/15 dark:border-white/15">
                {[
                  { value: "$35", label: "Hourly" },
                  { value: "$3.5k", label: "Projects from" },
                  { value: "8+", label: "Shipped builds" },
                  { value: "5+ yrs", label: "Production work" },
                ].map((s) => (
                  <motion.div
                    key={s.label}
                    variants={
                      reduce
                        ? undefined
                        : {
                            hidden: { opacity: 0, y: 12 },
                            show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                          }
                    }
                    className="bg-white dark:bg-black p-5"
                  >
                    <p className="font-display text-3xl font-extrabold text-black dark:text-white">{s.value}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500 dark:text-stone-500 leading-relaxed">
                      {s.label}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
